// ###### IMPORTS
import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { ArrowPathIcon } from '@heroicons/react/24/solid';
import { updateSnapshotPositions, updateSnapshotMarketInfo } from '../../viewServices/assets_api';
import ModalConfirm from 'views/finance/moduleComponents/ModalConfirm';

// ##### MAIN COMPONENT
const CardAssetsUpdateData = ({ reload }) => {
  const [isUpdatingPositions, setIsUpdatingPositions] = useState(false);
  const [showUpdateConfirmModal, setShowUpdateConfirmModal] = useState(false);
  const [isUpdatingMarketInfo, setIsUpdatingMarketInfo] = useState(false);
  const [showUpdateMarketInfoModal, setShowUpdateMarketInfoModal] = useState(false);

  const handleConfirmUpdateSnapshotPositions = async () => {
    setIsUpdatingPositions(true);
    try {
      const res = await updateSnapshotPositions();
      const data = res.data || {};
      toast.success(
        `Positions updated: ${data.updated_count ?? 0} item(s) across ${data.snapshots_processed ?? 0} snapshot(s).`
      );
      setShowUpdateConfirmModal(false);
      if (reload) reload();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to update positions.');
    } finally {
      setIsUpdatingPositions(false);
    }
  };

  const handleConfirmUpdateSnapshotMarketInfo = async () => {
    setIsUpdatingMarketInfo(true);
    try {
      const res = await updateSnapshotMarketInfo();
      const data = res.data || {};
      const errCount = (data.errors || []).length;
      if (errCount > 0) {
        toast.success(
          `${data.updated_count ?? 0} item(s) updated across ${data.snapshots_processed ?? 0} snapshot(s). ${errCount} failure(s).`
        );
      } else {
        toast.success(
          `Market info updated: ${data.updated_count ?? 0} item(s) across ${data.snapshots_processed ?? 0} snapshot(s).`
        );
      }
      setShowUpdateMarketInfoModal(false);
      if (reload) reload();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to update market info.');
    } finally {
      setIsUpdatingMarketInfo(false);
    }
  };

  const buttonBase = 'px-6 py-3 rounded-xl shadow-md text-white';
  const disabledClass = 'bg-gray-400 cursor-not-allowed';
  const enabledClass = 'bg-emerald-600 hover:bg-emerald-700';

  return (
    <>
      <div className="h-auto bg-white shadow rounded-xl p-4 flex flex-col gap-4">
        <div className="bg-green-800 border-b border-gray-200 -m-4 mb-4 rounded-t-xl">
          <div className="px-3 py-2 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <ArrowPathIcon className="h-4 w-4 text-white" />
              <h2 className="text-sm font-medium text-white">Recalculate Past Snapshots</h2>
            </div>
          </div>
        </div>

        <p className="text-sm text-gray-600">
          Recompute derived fields from existing records (operations, etc.) for past snapshots.
        </p>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowUpdateConfirmModal(true)}
            disabled={isUpdatingPositions || isUpdatingMarketInfo}
            className={`${buttonBase} ${isUpdatingPositions || isUpdatingMarketInfo ? disabledClass : enabledClass}`}
            title="Recalculate snapshot_value and snapshot_quantity from operations (tradeable assets)"
          >
            {isUpdatingPositions ? 'Updating…' : 'Update snapshot positions'}
          </button>
          <button
            onClick={() => setShowUpdateMarketInfoModal(true)}
            disabled={isUpdatingPositions || isUpdatingMarketInfo}
            className={`${buttonBase} ${isUpdatingPositions || isUpdatingMarketInfo ? disabledClass : 'bg-sky-600 hover:bg-sky-700'}`}
            title="Refresh market_unitary_value and market_source (Yahoo Finance B3) for items whose asset has is_updated and external_ticker"
          >
            {isUpdatingMarketInfo ? 'Updating…' : 'Update snapshot market info'}
          </button>
        </div>
      </div>

      <ModalConfirm
        isOpen={showUpdateConfirmModal}
        onClose={() => !isUpdatingPositions && setShowUpdateConfirmModal(false)}
        onConfirm={handleConfirmUpdateSnapshotPositions}
        title="Update snapshot positions"
        message="Quantity and value (snapshot_quantity and snapshot_value) will be recalculated for all snapshots and tradeable assets from operations. Continue?"
        confirmText={isUpdatingPositions ? 'Updating…' : 'Update'}
        cancelText="Cancel"
        confirmButtonClass={`px-4 py-2 rounded-xl ${isUpdatingPositions ? 'bg-gray-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'} text-white shadow`}
        cancelButtonClass={`px-4 py-2 rounded-xl border border-gray-300 ${isUpdatingPositions ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
        disabled={isUpdatingPositions}
      />
      <ModalConfirm
        isOpen={showUpdateMarketInfoModal}
        onClose={() => !isUpdatingMarketInfo && setShowUpdateMarketInfoModal(false)}
        onConfirm={handleConfirmUpdateSnapshotMarketInfo}
        title="Update snapshot market info"
        message="market_unitary_value and market_source (Yahoo Finance B3) will be refreshed for snapshot lines whose asset has is_updated and external_ticker. The date used is the snapshot date or the previous trading day. Continue?"
        confirmText={isUpdatingMarketInfo ? 'Updating…' : 'Update'}
        cancelText="Cancel"
        confirmButtonClass={`px-4 py-2 rounded-xl ${isUpdatingMarketInfo ? 'bg-gray-400 cursor-not-allowed' : 'bg-sky-600 hover:bg-sky-700'} text-white shadow`}
        cancelButtonClass={`px-4 py-2 rounded-xl border border-gray-300 ${isUpdatingMarketInfo ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
        disabled={isUpdatingMarketInfo}
      />
    </>
  );
};

export default CardAssetsUpdateData;
